/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// src/components/ReAssignedJobs.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Accordion,
  Button,
  Spinner,
  Alert,
  Modal,
  Form,
  Row,
  Col,
  Badge
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useLeadNotifications from '../hooks/useLeadNotifications';

// ── Helper functions ───────────────────────────────────
function formatWhen(iso) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric', minute: '2-digit', hour12: true,
    weekday: 'short', day: 'numeric', month: 'short',
    timeZone: 'Europe/London'
  }).formatToParts(new Date(iso));
  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return `${map.hour}:${map.minute} ${map.dayPeriod?.toLowerCase()}, ${map.weekday} ${map.day} ${map.month}`;
}

function extractTowns(from, to) {
  const town = (addr) => {
    if (!addr) return 'Unknown';
    const parts = addr.split(',').map(p => p.trim());
    return parts[parts.length - 1] || addr;
  };
  return `${town(from)} → ${town(to)}`;
}

const formatTimestamp = iso => {
  if (!iso) return 'Not available';
  const dt = new Date(iso);
  const hours = dt.getHours().toString().padStart(2, '0');
  const minutes = dt.getMinutes().toString().padStart(2, '0');
  const day = dt.getDate().toString().padStart(2, '0');
  const month = dt.toLocaleDateString('en-GB', { month: 'short', timeZone: 'Europe/London' });
  const year = dt.getFullYear().toString().slice(-2);
  return `${hours}:${minutes}-${day}/${month}/${year}`;
};

export default function ReAssignedJobs() {
  // ── Fire up your socket listener too ──
  useLeadNotifications();

  // ── Navigation state ─────────────────────────────────
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const navigate = useNavigate();
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  const navCollapse = () => {
    document.querySelectorAll('.navbar-collapse.show').forEach(el => el.classList.remove('show'));
    setAdminDropdownOpen(false);
  };

  const toggleAdminDropdown = (e) => {
    e.preventDefault();
    setAdminDropdownOpen(!adminDropdownOpen);
  };

  const logout = () => {
    localStorage.clear();
    navigate('/login');
  };

  // ── State ───────────────────────────────────────────
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);

  // ── CSV helpers ──────────────────────────────────────
  const handleDownloadCsv = () => {
    const headers = [
      'Customer Name', 'Phone', 'Email', 'From', 'To', 'Date/Time',
      'Passengers', 'Return Trip', 'Dog', 'Disabled', 'Notes',
      'Reassigned By', 'Reassigned At', 'Reassigned To', 'Accepted At'
    ];
    
    // Use filteredJobs if search is active, otherwise use all jobs
    const jobsToExport = searchTerm.trim() ? filteredJobs : jobs;
    const csvData = jobsToExport.map(job => [
      job.customerName || '',
      job.customerPhone || '',
      job.customerEmail || '',
      job.from || '',
      job.to || '',
      formatWhen(job.dateTime),
      job.passengers || '',
      job.returnTrip ? 'Yes' : 'No',
      job.dog ? 'Yes' : 'No',
      job.disabled ? 'Yes' : 'No',
      job.notes || '',
      job.reassignedBy?.name || job.reassignedBy?.email || '',
      job.reassignedAt ? formatWhen(job.reassignedAt) : '',
      job.reassignedTo?.name || job.reassignedTo?.email || '',
      job.acceptedAt ? formatWhen(job.acceptedAt) : ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reassigned-jobs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleEmailCsv = async () => {
    try {
      // Send filtered job IDs if search is active, otherwise send empty array to get all jobs
      const jobIds = searchTerm.trim() ? filteredJobs.map(j => j._id) : [];
      await axios.post('/view-leads/reassigned/email-csv', { jobIds });
      toast.success('CSV email queued. Check your inbox.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to queue CSV email.');
    }
  };

  // ── Fetch reassigned jobs ────────────────────────────
  useEffect(() => {
    axios.get('/view-leads/reassigned')
      .then(({ data }) => {
        console.log('🔍 REASSIGNED JOBS API DATA:', data.length, 'jobs');
        const testJob = data.find(j => j._id === '68af89507a914b2930a347ba');
        if (testJob) {
          console.log('🔍 TEST JOB FROM REASSIGNED API:', {
            id: testJob._id,
            customerName: testJob.customerName,
            dateTime: testJob.dateTime,
            status: testJob.status,
            reassignedBy: testJob.reassignedBy?._id
          });
        }
        setJobs(data);
        setFilteredJobs(data);
      })
      .catch(err => {
        console.error(err);
        setStatus('Failed to load reassigned jobs.');
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Search functionality ────────────────────────────
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredJobs(jobs);
      return;
    }

    const filtered = jobs.filter(job => 
      job.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.from?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatWhen(job.dateTime).toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.reassignedBy?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.reassignedBy?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.reassignedTo?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.reassignedTo?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredJobs(filtered);
  }, [searchTerm, jobs]);

  // Handle scroll to control sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Date buckets ────────────────────────────────────
  const now            = new Date();
  const startToday     = new Date(now.setHours(0,0,0,0));
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  const startWeekAgo   = new Date(startToday);
  startWeekAgo.setDate(startWeekAgo.getDate() - 7);

  const todayJobs     = filteredJobs.filter(j => new Date(j.dateTime) >= startToday);
  const yesterdayJobs = filteredJobs.filter(j => {
    const d = new Date(j.dateTime);
    return d >= startYesterday && d < startToday;
  });
  const lastWeekJobs  = filteredJobs.filter(j => {
    const d = new Date(j.dateTime);
    return d >= startWeekAgo && d < startYesterday;
  });
  const olderJobs     = filteredJobs.filter(j => new Date(j.dateTime) < startWeekAgo);

  const showJobTimestamps = (job) => {
    const timestamps = [
      `📅 Job Date/Time: ${formatTimestamp(job.dateTime)}`,
      `📝 Created: ${formatTimestamp(job.dateSubmitted)}`,
      job.updatedAt ? `✏️ Last Modified: ${formatTimestamp(job.updatedAt)}` : null,
      job.acceptedAt ? `✅ Accepted: ${formatTimestamp(job.acceptedAt)}` : null,
      job.reassignedAt ? `🔄 Reassigned: ${formatTimestamp(job.reassignedAt)}` : null,
      job.completedAt ? `🏁 Completed: ${formatTimestamp(job.completedAt)}` : null
    ].filter(Boolean);

    const timestampText = timestamps.join('\n');
    toast.info(timestampText, {
      position: "top-center",
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      style: { whiteSpace: 'pre-line' }
    });
  };

  // ── Render one job item ─────────────────────────────
  const renderItem = (job, idx, allJobs = [], sectionType = '') => {
    // Check if this is the first job of a new day (for Last Week and Older sections)
    const isFirstJobOfNewDay = (sectionType === 'lastWeek' || sectionType === 'older') && 
      allJobs.length > 0 && 
      idx > 0 && 
      allJobs[idx - 1] && 
      new Date(job.dateTime).toDateString() !== new Date(allJobs[idx - 1].dateTime).toDateString();

    // Determine if job is outgoing (reassigned by current user) or incoming (reassigned to current user)
    const currentUserId = localStorage.getItem('userId');
    const isOutgoing = job.reassignedBy?._id === currentUserId || job.reassignedBy?._id?.toString() === currentUserId;
    const isIncoming = job.reassignedTo?._id === currentUserId || job.reassignedTo?._id?.toString() === currentUserId;
    
    // Get company/driver name for display
    const getDisplayName = (driver) => {
      return driver?.companyName || driver?.name || driver?.email || 'Driver';
    };

    return (
    <Accordion.Item 
      key={job._id} 
      eventKey={String(idx)}
      className={isFirstJobOfNewDay ? 'day-change-border' : ''}
      style={isFirstJobOfNewDay ? { borderTop: '3px solid #000' } : {}}
    >
      <Accordion.Header>
        <div className="d-flex flex-column w-100">
          {/* Outgoing Re-assigned Job header (reassigned TO another driver) */}
          {isOutgoing && (
            <div className="bg-warning text-dark mb-0 rounded-top" style={{ marginTop: '-17px', marginLeft: '-21px', marginRight: '-42px', padding: '0.015rem' }}>
              <strong style={{ fontSize: '0.7rem' }}>Re-assigned to {getDisplayName(job.reassignedTo || job.acceptedBy)} {'>>>>'}</strong>
            </div>
          )}
          
          {/* Incoming Re-assigned Job header (reassigned FROM another driver) */}
          {isIncoming && (
            <div className="bg-info text-white mb-0 rounded-top" style={{ marginTop: '-17px', marginLeft: '-21px', marginRight: '-42px', padding: '0.015rem' }}>
              <strong style={{ fontSize: '0.7rem' }}>Re-assigned from {getDisplayName(job.reassignedBy)} {'<<<<'}</strong>
            </div>
          )}
          
          {/* Fallback header if neither condition matches */}
          {!isOutgoing && !isIncoming && (
            <div className="bg-secondary text-white mb-0 rounded-top" style={{ marginTop: '-17px', marginLeft: '-21px', marginRight: '-42px', padding: '0.015rem' }}>
              <strong style={{ fontSize: '0.7rem' }}>🔄 Re-assigned</strong>
            </div>
          )}
          
          <div className="d-flex align-items-center gap-2">
            <strong>{formatWhen(job.dateTime)}</strong>
          </div>
          <small>{extractTowns(job.from, job.to)}</small>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <p><strong>Customer:</strong> {job.customerName?.split(' ')[0] || job.customerName}</p>
        <p><strong>Route:</strong> {job.from} → {job.to}</p>
        <div className="d-flex align-items-center mb-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="me-2">📞</span>
          <span className="flex-grow-1">{job.customerPhone}</span>
        </div>
        <div className="d-flex align-items-center mb-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="me-2">📧</span>
          <span className="flex-grow-1">{job.customerEmail}</span>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <p className="mb-0"><strong>Passengers:</strong> {job.passengers}</p>
          <Button 
            variant="outline-info" 
            size="sm" 
            onClick={() => showJobTimestamps(job)}
            title="View Job Timestamps"
            className="rounded-circle"
            style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', width: '1.5rem', height: '1.5rem', backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#000' }}
          >
            i
          </Button>
        </div>
        {(job.dog || job.disabled) && (
          <div className="mt-2">
            {job.dog && <span className="badge bg-warning me-2">🐕 Dog/Pet</span>}
            {job.disabled && <span className="badge bg-info me-2">♿ Disabled Access</span>}
          </div>
        )}
        {job.notes && (
          <p className="mt-2"><strong>Notes:</strong> {job.notes}</p>
        )}
        <div className="mt-3 p-2 bg-light rounded">
          <h6 className="mb-2">🔄 Re-assignment Details</h6>
          <p className="mb-1"><strong>From:</strong> {job.reassignedBy?.companyName || job.reassignedBy?.name || job.reassignedBy?.email || 'Unknown'}</p>
          <p className="mb-1"><strong>To:</strong> {job.reassignedTo?.companyName || job.reassignedTo?.name || job.reassignedTo?.email || 'Unknown'}</p>
          <p className="mb-1"><strong>Re-assigned:</strong> {job.reassignedAt ? formatWhen(job.reassignedAt) : 'Unknown'}</p>
          {job.acceptedAt && (
            <p className="mb-0"><strong>Accepted:</strong> {formatWhen(job.acceptedAt)}</p>
          )}
        </div>
      </Accordion.Body>
    </Accordion.Item>
    );
  };

  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" />
        <p className="mt-2">Loading reassigned jobs...</p>
      </div>
    );
  }

  if (status) {
    return <Alert variant="danger">{status}</Alert>;
  }

  return (
    <>
      <ToastContainer position="top-center" />
      <div className="container mt-0" style={{ marginTop: '120px', paddingTop: '80px' }}>
        {/* Fixed Header - Page Content Only */}
        <div className="position-fixed bg-white border-bottom shadow-sm" style={{ 
          zIndex: 1029,
          top: '50px', // Position below the main navbar
          left: '0',
          right: '0',
          paddingTop: '8px',
          paddingBottom: '0px',
          borderTop: '2px solid #f8f9fa'
        }}>
          <div className="container">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">Re-Assigned Jobs</h4>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Button size="sm" variant="outline-primary" onClick={handleEmailCsv} title="Email CSV">📧</Button>
              </div>
            </div>
            
            {/* Search Bar below icons */}
            <div className="mt-2 position-relative" style={{ maxWidth: 500 }}>
              <Form.Control
                type="text"
                placeholder="Search jobs by customer, location, driver, or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ 
                  fontSize: '0.875rem',
                  paddingRight: '2rem' 
                }}
              />
              {searchTerm && (
                <Button
                  variant="link"
                  className="position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                  tabIndex={0}
                  style={{ fontSize: '1.5rem', lineHeight: 1, textDecoration: 'none' }}
                >
                  ×
                </Button>
              )}
            </div>
          </div>
        </div>

      {status && <Alert variant="info">{status}</Alert>}

      {todayJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Today</h6>
        <Accordion alwaysOpen={false}>{todayJobs.map(renderItem)}</Accordion>
      </>}

      {yesterdayJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Yesterday</h6>
        <Accordion alwaysOpen={false}>{yesterdayJobs.map(renderItem)}</Accordion>
      </>}

      {lastWeekJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Last Week</h6>
        <Accordion alwaysOpen={false}>{lastWeekJobs.map((job, idx) => renderItem(job, idx, lastWeekJobs, 'lastWeek'))}</Accordion>
      </>}

      {olderJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Older</h6>
        <Accordion alwaysOpen={false}>{olderJobs.map((job, idx) => renderItem(job, idx, olderJobs, 'older'))}</Accordion>
      </>}

      {filteredJobs.length === 0 && <p>No reassigned jobs found.</p>}
      </div>
    </>
  );
}
